// Detalle de pedido para Administración — SOLO LECTURA.
// Muestra composición (productos), cliente, estado, responsables y bultos/paquetes.
// Sin acciones (Marcela no interviene; solo consulta).

import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../../firebase";
import { getCatalogoByCodUru } from "../services/catalogo";
import { fixEncoding } from "utils/text";
import { PARTE_LABEL, parteDeProducto } from "../services/categoriasOverride";

function toURUCode(value) {
  const s = String(value ?? "").trim();
  const m = s.match(/\b\d{7,}\b/);
  return m ? m[0] : s.split(/\s+/)[0].replace(/[^\w-]/g, "");
}
// Nombre del producto: Customer No del catálogo primero; si no, la descripción de Finnegans.
function nombreProducto(it, cat) {
  const catNombre = cat?.customerNo || cat?.customer_no || "";
  if (catNombre) return fixEncoding(catNombre);
  return fixEncoding(it.descripcion || it.desc || it.nombre || it.cod || it.codigo || "Producto");
}
function reqQty(it) { return it.cant ?? it.cantidad ?? it.qty ?? 0; }

const ESTADO_LABEL = {
  PENDIENTE_ASIGNAR: "Pendiente de asignar", ASIGNADO: "Asignado", EN_PREPARACION: "En preparación",
  CON_ERROR: "Con error", PREPARADO: "Preparado", CONTROLADO: "Controlado", DESPACHADO: "Despachado", ENTREGADO: "Entregado",
};

export default function SeguimientoPedidoDetalle() {
  const { id: rawId } = useParams();
  const id = useMemo(() => decodeURIComponent(rawId || ""), [rawId]);
  const navigate = useNavigate();
  const [pedido, setPedido] = useState(null);
  const [loading, setLoading] = useState(true);
  const [catalogoMap, setCatalogoMap] = useState({});

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        for (const col of ["pedidos", "pedidos_despachados", "pedidos_entregados"]) {
          const snap = await getDoc(doc(db, col, id));
          if (snap.exists()) { setPedido({ id: snap.id, _col: col, ...snap.data() }); break; }
        }
      } catch (e) { console.error("[seguimiento detalle]", e); }
      finally { setLoading(false); }
    })();
  }, [id]);

  useEffect(() => {
    const prods = pedido?.productos;
    if (!Array.isArray(prods) || prods.length === 0) return;
    const codigos = [...new Set(prods.map(p => toURUCode(p.cod || p.codigo || p.codigoURU || p.desc || p.descripcion || p.nombre)).filter(Boolean))];
    let cancel = false;
    (async () => {
      try {
        const entries = await Promise.all(codigos.map(async u => [u, (await getCatalogoByCodUru(u)) || null]));
        if (!cancel) setCatalogoMap(Object.fromEntries(entries));
      } catch (e) { console.error("[seguimiento catálogo]", e); }
    })();
    return () => { cancel = true; };
  }, [pedido?.productos]);

  if (loading) return <div style={{ padding: "24px", textAlign: "center", color: "#94a3b8" }}>Cargando pedido…</div>;
  if (!pedido) return (
    <div style={{ padding: "24px", textAlign: "center" }}>
      <p style={{ color: "#ef4444" }}>Pedido no encontrado.</p>
      <button onClick={() => navigate("/administracion/seguimiento")} style={{ marginTop: "10px", background: "none", border: "none", color: "#0f766e", fontWeight: 600, cursor: "pointer" }}>← Volver al seguimiento</button>
    </div>
  );

  const productos = Array.isArray(pedido.productos) ? pedido.productos : [];
  const asg = pedido.asignaciones || null;

  return (
    <div style={{ background: "#f8fafc", minHeight: "100vh", paddingBottom: "40px" }}>
      <div style={{ background: "#fff", borderBottom: "1px solid #e2e8f0", padding: "12px 16px" }}>
        <button onClick={() => navigate("/administracion/seguimiento")} style={{ background: "none", border: "none", color: "#0f766e", fontWeight: 600, fontSize: "0.85rem", padding: 0, cursor: "pointer" }}>
          ← Volver al seguimiento
        </button>
        <div style={{ marginTop: "10px", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "8px" }}>
          <div>
            <p style={{ margin: 0, fontSize: "0.72rem", fontWeight: 700, color: "#94a3b8", letterSpacing: "0.06em", textTransform: "uppercase" }}>Pedido #{pedido.numero || id}</p>
            <h1 style={{ margin: "2px 0 0", fontSize: "1.25rem", fontWeight: 800, color: "#0f172a" }}>{pedido.cliente || "—"}</h1>
          </div>
          <span style={{ flexShrink: 0, background: "#f1f5f9", color: "#475569", fontSize: "0.68rem", fontWeight: 700, padding: "4px 10px", borderRadius: "999px", marginTop: "4px" }}>
            {ESTADO_LABEL[pedido.estado] || pedido.estado || "—"}
          </span>
        </div>
        <div style={{ marginTop: "8px", display: "flex", flexWrap: "wrap", gap: "6px" }}>
          {pedido.metodoEntrega && <span style={chip}>🚚 {pedido.metodoEntrega}</span>}
          {pedido.deposito && <span style={chip}>🏭 {pedido.deposito}</span>}
          {pedido.bultos > 0 && <span style={chip}>📦 {pedido.bultos} bultos</span>}
          {pedido.paquetes > 0 && <span style={chip}>📏 {pedido.paquetes} paquetes</span>}
        </div>
      </div>

      <div style={{ padding: "16px" }}>
        {/* Responsables por parte (si el pedido estuvo dividido) */}
        {asg && Object.keys(asg).length > 0 && (
          <div style={{ marginBottom: "16px" }}>
            <p style={{ margin: "0 0 8px", fontSize: "0.7rem", fontWeight: 700, color: "#94a3b8", letterSpacing: "0.06em", textTransform: "uppercase" }}>Responsables</p>
            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              {Object.keys(asg).map(parte => (
                <div key={parte} style={{ display: "flex", alignItems: "center", gap: "8px", background: "#fff", border: "1px solid #e2e8f0", borderRadius: "10px", padding: "8px 12px" }}>
                  <span style={{ fontSize: "0.74rem", fontWeight: 700, color: "#5b21b6", background: "#f5f3ff", padding: "2px 8px", borderRadius: "999px" }}>{PARTE_LABEL[parte] || parte}</span>
                  <span style={{ fontSize: "0.85rem", color: "#334155", fontWeight: 600 }}>👤 {asg[parte]?.nombre || "—"}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        {!asg && pedido.operarioNombre && (
          <div style={{ marginBottom: "16px" }}>
            <p style={{ margin: "0 0 8px", fontSize: "0.7rem", fontWeight: 700, color: "#94a3b8", letterSpacing: "0.06em", textTransform: "uppercase" }}>Responsable</p>
            <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: "10px", padding: "8px 12px", fontSize: "0.9rem", fontWeight: 600, color: "#334155" }}>👤 {pedido.operarioNombre}</div>
          </div>
        )}

        {/* Composición */}
        <p style={{ margin: "0 0 8px", fontSize: "0.7rem", fontWeight: 700, color: "#94a3b8", letterSpacing: "0.06em", textTransform: "uppercase" }}>
          Composición · {productos.length} producto{productos.length !== 1 ? "s" : ""}
        </p>
        <div style={{ background: "#fff", border: "1.5px solid #e2e8f0", borderRadius: "14px", padding: "6px 14px" }}>
          {productos.length === 0 ? (
            <p style={{ margin: 0, padding: "10px 0", color: "#94a3b8", fontSize: "0.85rem" }}>Sin productos registrados.</p>
          ) : productos.map((it, i) => {
            const uru = toURUCode(it.cod || it.codigo || it.codigoURU || it.desc || it.descripcion || it.nombre);
            const cat = catalogoMap[uru] || {};
            const color = cat?.finish || cat?.color || "";
            return (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: "10px", padding: "10px 0", borderBottom: i < productos.length - 1 ? "1px solid #f1f5f9" : "none" }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ margin: 0, fontSize: "0.88rem", fontWeight: 600, color: "#1e293b", wordBreak: "break-word" }}>{nombreProducto(it, cat)}</p>
                  {color && <p style={{ margin: "2px 0 0", fontSize: "0.74rem", color: "#94a3b8" }}>{color}</p>}
                </div>
                <span style={{ flexShrink: 0, background: "#f1f5f9", color: "#475569", fontWeight: 700, fontSize: "0.82rem", padding: "3px 10px", borderRadius: "8px" }}>×{reqQty(it)}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

const chip = { fontSize: "0.75rem", color: "#64748b", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "6px", padding: "2px 8px" };
